import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { authApi, clearToken, formatApiErrorDetail } from "@/lib/api";
import { LogOut, RefreshCw, Trash2, Mail, Phone, MapPin, Calendar, Briefcase, FileText } from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { id: "leads", label: "Leads", Icon: FileText },
  { id: "brokers", label: "Brokers", Icon: Briefcase },
];

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [leads, setLeads] = useState([]);
  const [brokers, setBrokers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [tab, setTab] = useState("leads");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [meRes, leadsRes, brokersRes] = await Promise.all([
        authApi.get("/auth/me"),
        authApi.get("/leads"),
        authApi.get("/brokers"),
      ]);
      setMe(meRes.data);
      setLeads(leadsRes.data);
      setBrokers(brokersRes.data);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        clearToken();
        navigate("/admin/login", { replace: true });
        return;
      }
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogout = () => {
    clearToken();
    navigate("/admin/login", { replace: true });
  };

  const handleDeleteLead = async (id) => {
    if (!window.confirm("Delete this lead? This cannot be undone.")) return;
    try {
      await authApi.delete(`/leads/${id}`);
      setLeads((ls) => ls.filter((l) => l.id !== id));
      toast.success("Lead deleted");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed");
    }
  };

  const handleDeleteBroker = async (id) => {
    if (!window.confirm("Delete this broker application? This cannot be undone.")) return;
    try {
      await authApi.delete(`/brokers/${id}`);
      setBrokers((bs) => bs.filter((b) => b.id !== id));
      toast.success("Broker deleted");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed");
    }
  };

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
    } catch {
      return iso;
    }
  };

  // Active list + filtering
  const active = tab === "leads" ? leads : brokers;
  const filtered = active.filter((item) => {
    if (!query) return true;
    const q = query.toLowerCase();
    if (tab === "leads") {
      return [item.full_name, item.email, item.phone, item.loan_type, item.property_address]
        .filter(Boolean)
        .some((f) => f.toLowerCase().includes(q));
    }
    return [item.first_name, item.last_name, item.company, item.email, item.phone, item.states_licensed, item.nmls]
      .filter(Boolean)
      .some((f) => String(f).toLowerCase().includes(q));
  });

  // Stats per active tab
  const now = Date.now();
  const within = (iso, ms) => now - new Date(iso).getTime() < ms;
  const stats = {
    total: active.length,
    last7: active.filter((x) => within(x.created_at, 7 * 24 * 3600 * 1000)).length,
    last24h: active.filter((x) => within(x.created_at, 24 * 3600 * 1000)).length,
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="admin-dashboard">
      <header className="border-b border-neutral-800 sticky top-0 bg-[#0A0A0A]/95 backdrop-blur z-10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-[72px] flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/brand/groundup-logo-light.png" alt="GroundUp Lending" className="h-8 w-auto" />
            <span className="label-overline text-neutral-500 hidden sm:inline">Admin</span>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={load} className="text-sm text-neutral-400 hover:text-[#22C55E] flex items-center gap-2" data-testid="refresh-btn">
              <RefreshCw size={14} /> Refresh
            </button>
            <a href="/" className="text-sm text-neutral-400 hover:text-[#22C55E]">View site</a>
            <button onClick={handleLogout} className="text-sm text-neutral-400 hover:text-red-400 flex items-center gap-2" data-testid="logout-btn">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <div className="mb-10">
          <div className="label-overline text-[#22C55E] mb-3">Admin Dashboard</div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight">
            Welcome back{me?.name ? `, ${me.name}` : ""}.
          </h1>
          {me?.email && <p className="text-neutral-400 mt-2">{me.email}</p>}
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-px bg-neutral-800 border border-neutral-800 mb-8 max-w-md" data-testid="admin-tabs">
          {TABS.map((t) => {
            const Icon = t.Icon;
            const counts = t.id === "leads" ? leads.length : brokers.length;
            const isActive = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`flex-1 px-5 py-4 flex items-center justify-center gap-2 font-heading font-semibold transition-colors ${
                  isActive ? "bg-[#22C55E] text-[#0A0A0A]" : "bg-[#0A0A0A] text-neutral-400 hover:text-white"
                }`}
                data-testid={`admin-tab-${t.id}`}
              >
                <Icon size={16} /> {t.label}
                <span className={`ml-1 text-xs ${isActive ? "text-[#0A0A0A]/70" : "text-neutral-600"}`}>({counts})</span>
              </button>
            );
          })}
        </div>

        {/* Stats */}
        <div className="grid sm:grid-cols-3 gap-px bg-neutral-800 border border-neutral-800 mb-10">
          {[
            { k: stats.total, v: tab === "leads" ? "Total Leads" : "Total Brokers" },
            { k: stats.last7, v: "Last 7 Days" },
            { k: stats.last24h, v: "Last 24 Hours" },
          ].map((s) => (
            <div key={s.v} className="bg-[#0A0A0A] p-6">
              <div className="label-overline text-neutral-500 mb-2">{s.v}</div>
              <div className="font-heading text-4xl font-bold">{s.k}</div>
            </div>
          ))}
        </div>

        <div className="mb-6 flex items-center justify-between gap-4 flex-wrap">
          <h2 className="font-heading text-2xl font-bold">
            {tab === "leads" ? "All Lead Submissions" : "All Broker Applications"}
          </h2>
          <input
            type="text"
            placeholder={tab === "leads" ? "Search name, email, phone…" : "Search name, company, NMLS…"}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="form-input max-w-sm"
            data-testid="admin-search"
          />
        </div>

        {loading ? (
          <div className="border border-neutral-800 p-10 text-center text-neutral-500" data-testid="admin-loading">Loading…</div>
        ) : error ? (
          <div className="border border-red-800 bg-red-950/20 p-6 text-red-300">{error}</div>
        ) : filtered.length === 0 ? (
          <div className="border border-neutral-800 p-10 text-center text-neutral-500" data-testid="admin-empty">
            {query ? "No matches." : tab === "leads" ? "No leads yet." : "No broker applications yet."}
          </div>
        ) : tab === "leads" ? (
          <div className="grid gap-px bg-neutral-800 border border-neutral-800" data-testid="leads-list">
            {filtered.map((l) => (
              <div key={l.id} className="bg-[#0A0A0A] p-6 md:p-8 hover:bg-[#111] transition-colors" data-testid={`lead-row-${l.id}`}>
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-4">
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="font-heading text-xl font-bold">{l.full_name}</h3>
                      <span className="label-overline text-[#22C55E] border border-[#22C55E]/40 px-2 py-0.5">{l.loan_type}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-neutral-500">
                      <Calendar size={12} /> {formatDate(l.created_at)}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {l.loan_amount && (
                      <div className="text-right">
                        <div className="label-overline text-neutral-500">Loan Amount</div>
                        <div className="font-heading text-xl font-bold text-[#22C55E]">{l.loan_amount}</div>
                      </div>
                    )}
                    <button onClick={() => handleDeleteLead(l.id)} className="p-2 text-neutral-500 hover:text-red-400" title="Delete" data-testid={`delete-lead-${l.id}`}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm text-neutral-300">
                  <a href={`mailto:${l.email}`} className="flex items-center gap-2 hover:text-[#22C55E]"><Mail size={14} className="text-neutral-500" /> {l.email}</a>
                  <a href={`tel:${l.phone}`} className="flex items-center gap-2 hover:text-[#22C55E]"><Phone size={14} className="text-neutral-500" /> {l.phone}</a>
                  {l.property_address && <div className="flex items-center gap-2"><MapPin size={14} className="text-neutral-500" /> {l.property_address}</div>}
                </div>
                {l.message && (
                  <div className="mt-4 pt-4 border-t border-neutral-800">
                    <div className="label-overline text-neutral-500 mb-2">Deal Details</div>
                    <p className="text-neutral-300 text-[15px] leading-relaxed whitespace-pre-wrap">{l.message}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="grid gap-px bg-neutral-800 border border-neutral-800" data-testid="brokers-list">
            {filtered.map((b) => (
              <div key={b.id} className="bg-[#0A0A0A] p-6 md:p-8 hover:bg-[#111] transition-colors" data-testid={`broker-row-${b.id}`}>
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-4">
                  <div>
                    <div className="flex items-center gap-3 mb-1 flex-wrap">
                      <h3 className="font-heading text-xl font-bold">{b.first_name} {b.last_name}</h3>
                      <span className="text-sm text-neutral-400">· {b.company}</span>
                      {b.nmls && <span className="label-overline text-[#22C55E] border border-[#22C55E]/40 px-2 py-0.5">NMLS {b.nmls}</span>}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-neutral-500">
                      <Calendar size={12} /> {formatDate(b.created_at)}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {b.monthly_volume && (
                      <div className="text-right">
                        <div className="label-overline text-neutral-500">Monthly Vol.</div>
                        <div className="font-heading text-lg font-bold text-[#22C55E]">{b.monthly_volume}</div>
                      </div>
                    )}
                    <button onClick={() => handleDeleteBroker(b.id)} className="p-2 text-neutral-500 hover:text-red-400" title="Delete" data-testid={`delete-broker-${b.id}`}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm text-neutral-300 mb-4">
                  <a href={`mailto:${b.email}`} className="flex items-center gap-2 hover:text-[#22C55E]"><Mail size={14} className="text-neutral-500" /> {b.email}</a>
                  <a href={`tel:${b.phone}`} className="flex items-center gap-2 hover:text-[#22C55E]"><Phone size={14} className="text-neutral-500" /> {b.phone}</a>
                  <div className="flex items-center gap-2"><MapPin size={14} className="text-neutral-500" /> {b.states_licensed}</div>
                </div>
                <div className="grid sm:grid-cols-3 gap-4 text-sm border-t border-neutral-800 pt-4">
                  <div>
                    <div className="label-overline text-neutral-500 mb-1">Experience</div>
                    <div className="text-neutral-200">{b.years_experience || "—"}</div>
                  </div>
                  <div>
                    <div className="label-overline text-neutral-500 mb-1">Loan Types</div>
                    <div className="text-neutral-200">{b.loan_types || "—"}</div>
                  </div>
                  <div>
                    <div className="label-overline text-neutral-500 mb-1">Referral Source</div>
                    <div className="text-neutral-200">{b.referral_source || "—"}</div>
                  </div>
                </div>
                {b.notes && (
                  <div className="mt-4 pt-4 border-t border-neutral-800">
                    <div className="label-overline text-neutral-500 mb-2">Notes</div>
                    <p className="text-neutral-300 text-[15px] leading-relaxed whitespace-pre-wrap">{b.notes}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
